import axios from 'axios';
import { LoginForm, User, ApiResponse } from '../types';
import { sanitizeEmail, sanitizeText } from '../utils/sanitize';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    } else if (error.response?.status === 429) {
      // Rate limiting error
      const retryAfter = error.response.headers['retry-after'];
      const errorMessage = retryAfter
        ? `Too many requests. Try again in ${retryAfter} seconds.`
        : 'Too many requests. Please wait before trying again.';
      error.rateLimited = true;
      error.userMessage = errorMessage;
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Login user
  async login(email: string, password: string): Promise<ApiResponse<{ access_token: string; user: User }>> {
    const sanitizedEmail = sanitizeEmail(email);
    const sanitizedPassword = sanitizeText(password);
    const response = await api.post('/auth/login', { email: sanitizedEmail, password: sanitizedPassword });
    return response;
  },

  // Get all users (admin only)
  async getUsers(): Promise<ApiResponse<User[]>> {
    const response = await api.get('/auth/users');
    return response;
  },

  // Create new user (admin only)
  async createUser(userData: Omit<User, 'id'> & { password: string }): Promise<ApiResponse<{ message: string }>> {
    const sanitizedData = {
      name: sanitizeText(userData.name),
      email: sanitizeEmail(userData.email),
      password: sanitizeText(userData.password),
      roles: userData.roles // roles is validated on backend
    };
    const response = await api.post('/auth/users', sanitizedData);
    return response;
  },

  // Update user (admin only)
  async updateUser(userId: number, userData: Partial<User & { password?: string }>): Promise<ApiResponse<{ message: string }>> {
    const response = await api.put(`/auth/users/${userId}`, userData);
    return response;
  },

  // Get current user profile
  async getProfile(): Promise<ApiResponse<{ user: User }>> {
    const response = await api.get('/auth/profile');
    return response;
  },

  // Change current user password
  async changePassword(oldPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    const sanitizedOldPassword = sanitizeText(oldPassword);
    const sanitizedNewPassword = sanitizeText(newPassword);
    const response = await api.put('/auth/change-password', { old_password: sanitizedOldPassword, new_password: sanitizedNewPassword });
    return response;
  },

  // Verify current user's password
  async verifyPassword(password: string): Promise<ApiResponse<{ verified: boolean }>> {
    const sanitizedPassword = sanitizeText(password);
    const response = await api.post('/auth/verify-password', { password: sanitizedPassword });
    return response;
  },
};

export default api;